import type { ReactNode } from 'react'
import { Settings as SettingsIcon } from 'lucide-react'
import {
  ToolbarAction,
  ToolbarCluster,
  ToolbarDivider,
  ToolbarFlow,
} from '@/components/molecules/EventToolbar'
import { DestinationsSheet } from '@/components/organisms/DestinationsSheet'
import { LiveKitConnectionStatus } from '@/components/livekit/EventLiveKitProvider'
import type { StreamResource } from '@/types/events'

interface EventWorkspaceActionsProps {
  eventId: string
  eventName: string
  /**
   * Page-specific primary actions, rendered as the left "Sources" cluster.
   * Examples: Add Camera + Invite (EventPage), layout switcher + share (MultiView).
   * Omit on pages that don't have a primary action of their own (Sponsors).
   */
  primary?: ReactNode
  /**
   * Streams powering the Destinations sheet (Public page status + OBS export).
   * Optional — pages without stream data still see the sheet, just with
   * reduced fidelity (badge count = 0, OBS row in "waiting" state). The public
   * page reads Live only with LiveKit video from a surrounding
   * `EventLiveKitProvider`; otherwise it reads Open.
   */
  streams?: StreamResource[]
  /** False when the event's public page is off; Destinations then says so instead of "Open". */
  publicPageEnabled?: boolean
  /** In-app path to the public page (`publicEventPath(event)`); Destinations shows and opens it. Required, as there. */
  publicPath: string
  /**
   * Render the LiveKit connection status pill. Requires the surrounding page
   * to be wrapped in `EventLiveKitProvider`. Defaults to true.
   */
  showConnectionStatus?: boolean
}

/**
 * Single source of truth for the event-workspace toolbar.
 *
 * Layout follows the "sources → destinations" spatial grammar:
 * - Left cluster: page-specific primary actions (sources of work)
 * - Flow arrow:   visually links the two halves
 * - Right cluster: Destinations + Status + Settings (the shared chrome)
 *
 * Used by EventPage, MultiViewPage and SponsorsPage so the
 * toolbar reads identically as users switch tabs.
 */
export function EventWorkspaceActions({
  eventId,
  eventName,
  primary,
  streams = [],
  publicPageEnabled,
  publicPath,
  showConnectionStatus = true,
}: EventWorkspaceActionsProps) {
  return (
    <>
      {primary && (
        <>
          <ToolbarCluster ariaLabel="Sources">{primary}</ToolbarCluster>
          <ToolbarFlow />
        </>
      )}
      <ToolbarCluster ariaLabel="Broadcasts">
        <DestinationsSheet
          eventName={eventName}
          streams={streams}
          publicPageEnabled={publicPageEnabled}
          publicPath={publicPath}
        />
        <ToolbarDivider />
        {showConnectionStatus && (
          <LiveKitConnectionStatus className="hidden sm:flex" />
        )}
        <ToolbarAction
          icon={<SettingsIcon />}
          label="Event settings"
          to={`/events/${eventId}/settings`}
        />
      </ToolbarCluster>
    </>
  )
}
